import type { ReactNode } from "react";
import { cn } from "utils";
import { InlineBoldText } from "./InlineBoldText";
import type {
  LayoutCardProps,
  LayoutGridProps,
  LayoutMetricProps,
  LayoutTextProps,
} from "./layoutCatalog";
import {
  LAYOUT_CARD_CLASS,
  TONE_TEXT_CLASS,
  pickEnum,
  resolveText,
  resolveTone,
} from "./layoutTokens";

type WithChildren<P> = Partial<P> & { children?: ReactNode };

export const LayoutCard: React.FC<WithChildren<LayoutCardProps>> = ({
  title,
  children,
}) => {
  const heading = resolveText(title);
  return (
    <div className={LAYOUT_CARD_CLASS}>
      {heading && (
        <h3 className="title_l break-words text-content_primary">{heading}</h3>
      )}
      {children}
    </div>
  );
};

export const LayoutGrid: React.FC<WithChildren<LayoutGridProps>> = ({
  columns,
  children,
}) => (
  <div
    className={cn(
      "grid w-full grid-cols-2 gap-3",
      columns === 3 && "md:grid-cols-3",
    )}
  >
    {children}
  </div>
);

export const LayoutText: React.FC<Partial<LayoutTextProps>> = ({
  text,
  size,
  tone,
  weight,
}) => {
  const content = resolveText(text);
  if (!content) {
    return null;
  }
  const small = pickEnum(size, ["sm", "md"], "md") === "sm";
  const semibold =
    pickEnum(weight, ["regular", "semibold"], "regular") === "semibold";
  const typography = small
    ? semibold
      ? "body_bold_s"
      : "body_s"
    : semibold
      ? "body_bold_m"
      : "body_m";
  return (
    <p
      className={cn(
        "min-w-0 whitespace-pre-wrap break-words",
        typography,
        TONE_TEXT_CLASS[resolveTone(tone)],
      )}
    >
      <InlineBoldText text={content} />
    </p>
  );
};

export const LayoutMetric: React.FC<Partial<LayoutMetricProps>> = ({
  label,
  value,
  caption,
  tone,
}) => {
  const labelText = resolveText(label);
  const valueText = resolveText(value);
  if (!labelText || !valueText) {
    return null;
  }
  const captionText = resolveText(caption);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="body_s break-words text-content_tertiary">
        {labelText}
      </span>
      <span
        className={cn(
          "title_xl break-words",
          TONE_TEXT_CLASS[resolveTone(tone)],
        )}
      >
        {valueText}
      </span>
      {captionText && (
        <span className="body_s break-words text-content_tertiary">
          {captionText}
        </span>
      )}
    </div>
  );
};
